/**
 * Synthetic legacy source for the migration v2 simulator (Admin > Migration v2 Simulator).
 * Same shape as the tool's MappedTeamEmployee after dedupe and HR's report, but every name,
 * email and code is generated from a seed: no real employee data ever reaches this page.
 */

export type SimStatus = "ACTIVE" | "ON_LEAVE" | "NOTICE_PERIOD" | "TERMINATED" | "CONTRACTOR";
export type SimType = "FULL_TIME" | "PART_TIME" | "CONTRACTOR" | "INTERN";
export type SimLocation = "NOIDA" | "TIRUPATI" | "HYDERABAD" | "BANGALORE" | "MUMBAI" | "INDIA_REMOTE" | "USA_HQ" | "USA_REMOTE";

export interface SimEmployee {
  legacyBayUserId: number;
  employeeCode:    string;
  legacyEmpId:     string | null;
  firstName:       string;
  lastName:        string;
  email:           string;
  status:          SimStatus;
  employeeType:    SimType;
  role:            string;
  workLocation:    SimLocation;
  officeId:        string;
  departmentName:  string;
  designation:     string;
  hireDate:        string;          // YYYY-MM-DD
  managerBayId:    number | null;
  // What the legacy tables hold for this person, so modules and verify have something to count.
  leaves:          number;
  badLeaves:       number;          // rows the leave module skips (blank type, zero days …)
  attendanceDays:  number;
  documents:       number;
  failsToLoad:     string | null;   // the error a load of this person throws, if any
}

export interface SourceSettings {
  size:        number;   // employees in the synthetic source
  seed:        number;   // same seed → same people
  failureRate: number;   // % of people whose load fails (shows done-with-errors)
}

export const defaultSourceSettings: SourceSettings = { size: 240, seed: 2026, failureRate: 2 };

// Same mulberry32 the tool's --order=random uses.
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const FIRST = ["Aarav", "Maya", "Rohan", "Ishita", "Kabir", "Nisha", "Vikram", "Tara", "Arjun", "Leela", "Dev", "Sana",
  "Noah", "Emma", "Liam", "Ava", "Ethan", "Mia", "Lucas", "Zoe", "Omar", "Priya", "Ravi", "Anya", "Kiran", "Meera",
  "Jonah", "Elena", "Samir", "Riya", "Owen", "Nora", "Aditya", "Kavya", "Felix", "Iris"];
const LAST = ["Sample", "Demo", "Testwell", "Placeholder", "Mockford", "Synth", "Fakhri", "Exampleton", "Dummett",
  "Trialson", "Pilotkar", "Sandbox", "Stubbs", "Fixture", "Seedman", "Proto", "Draftwala", "Quinlan", "Vantest", "Rehearsal"];

// Legacy departments and the role the tool maps them to (TEAM_ROLE_TO_HRMS).
const DEPARTMENTS: Array<{ name: string; role: string; weight: number; titles: string[] }> = [
  { name: "Technical - Wireless",      role: "ENGINEER",       weight: 26, titles: ["RF Engineer", "Wireless Engineer", "Site Engineer"] },
  { name: "Technical - OSP/Wiredline", role: "ENGINEER",       weight: 20, titles: ["OSP Engineer", "Fiber Designer", "Splicing Technician"] },
  { name: "Customer Service",          role: "ENGINEER",       weight: 12, titles: ["Support Associate", "Service Desk Analyst"] },
  { name: "Account Management",        role: "ENGINEER",       weight: 8,  titles: ["Account Manager", "Client Coordinator"] },
  { name: "Marketing",                 role: "ENGINEER",       weight: 4,  titles: ["Marketing Executive", "Content Writer"] },
  { name: "IT Department",             role: "REGIONAL_IT",    weight: 6,  titles: ["IT Administrator", "Systems Engineer"] },
  { name: "HR",                        role: "REGIONAL_HR",    weight: 5,  titles: ["HR Generalist", "HR Associate"] },
  { name: "Recruiting",                role: "REGIONAL_HR",    weight: 5,  titles: ["Recruiter", "Talent Sourcer"] },
  { name: "Accounts & Finance",        role: "FINANCE",        weight: 5,  titles: ["Accountant", "Finance Analyst"] },
  { name: "Admin",                     role: "REGIONAL_ADMIN", weight: 4,  titles: ["Office Administrator", "Facilities Executive"] },
  { name: "Executive Team",            role: "SR_MANAGER",     weight: 2,  titles: ["Director", "Vice President"] },
];

const LOCATIONS: Array<{ loc: SimLocation; office: string; weight: number; domain: string }> = [
  { loc: "NOIDA",        office: "Noida",     weight: 30, domain: "integertel.com" },
  { loc: "TIRUPATI",     office: "Tirupati",  weight: 15, domain: "integertel.com" },
  { loc: "HYDERABAD",    office: "Hyderabad", weight: 10, domain: "integertel.com" },
  { loc: "BANGALORE",    office: "Bangalore", weight: 6,  domain: "integertel.com" },
  { loc: "MUMBAI",       office: "Mumbai",    weight: 4,  domain: "integertel.com" },
  { loc: "INDIA_REMOTE", office: "Bangalore", weight: 8,  domain: "softql.com" },
  { loc: "USA_HQ",       office: "New York",  weight: 5,  domain: "integertel.com" },
  { loc: "USA_REMOTE",   office: "New York",  weight: 22, domain: "integertel.com" },
];

const LOAD_ERRORS = [
  "Unique constraint failed on the fields: (`email`)",
  "Unique constraint failed on the fields: (`employeeCode`)",
  "Invalid `prisma.employee.upsert()` invocation: hireDate is before 1990-01-01",
];

function weighted<T extends { weight: number }>(items: T[], r: number): T {
  const total = items.reduce((s, i) => s + i.weight, 0);
  let x = r * total;
  for (const i of items) { x -= i.weight; if (x < 0) return i; }
  return items[items.length - 1]!;
}

const pad = (n: number, w: number) => String(n).padStart(w, "0");

export function generateSource(settings: SourceSettings): SimEmployee[] {
  const rand = mulberry32(settings.seed);
  const pick = <T,>(xs: T[]) => xs[Math.floor(rand() * xs.length)]!;
  const out: SimEmployee[] = [];
  const usedEmails = new Set<string>();
  let bayId = 100;

  for (let i = 0; i < settings.size; i++) {
    bayId += 1 + Math.floor(rand() * 3);   // legacy ids have gaps, like bay_users
    const dept = weighted(DEPARTMENTS, rand());
    const place = weighted(LOCATIONS, rand());
    const firstName = pick(FIRST);
    const lastName = pick(LAST);
    let email = `${firstName}.${lastName}`.toLowerCase() + `@${place.domain}`;
    if (usedEmails.has(email)) email = `${firstName}.${lastName}${bayId}`.toLowerCase() + `@${place.domain}`;
    usedEmails.add(email);

    const kind = rand();
    let role = dept.role;
    if (role === "ENGINEER" && kind < 0.1) role = "TEAM_LEAD";
    if (kind > 0.95) role = "CONTRACTOR";
    else if (kind > 0.92 && dept.role === "ENGINEER") role = "INTERN";
    const employeeType: SimType = role === "CONTRACTOR" ? "CONTRACTOR" : role === "INTERN" ? "INTERN" : rand() < 0.04 ? "PART_TIME" : "FULL_TIME";

    const s = rand();
    const status: SimStatus = role === "CONTRACTOR" && s < 0.6 ? "CONTRACTOR"
      : s < 0.34 ? "TERMINATED" : s < 0.38 ? "NOTICE_PERIOD" : s < 0.41 ? "ON_LEAVE" : role === "CONTRACTOR" ? "CONTRACTOR" : "ACTIVE";

    const year = 2014 + Math.floor(rand() * 12);
    const hireDate = `${year}-${pad(1 + Math.floor(rand() * 12), 2)}-${pad(1 + Math.floor(rand() * 28), 2)}`;
    const years = Math.max(0.3, 2026 - year);
    const leaves = Math.floor(rand() * Math.min(40, years * 6));
    const noCode = rand() < 0.08;   // no Emp ID in the legacy row: the tool makes one from the bay id

    out.push({
      legacyBayUserId: bayId,
      employeeCode: noCode ? `BAY${bayId}` : `IT${pad(i + 1, 3)}`,
      legacyEmpId: noCode ? null : `IT${pad(i + 1, 4)}`,
      firstName, lastName, email, status, employeeType, role,
      workLocation: place.loc, officeId: place.office,
      departmentName: dept.name,
      designation: role === "TEAM_LEAD" ? "Team Lead" : role === "INTERN" ? "Intern" : pick(dept.titles),
      hireDate,
      managerBayId: null,
      leaves,
      badLeaves: leaves > 3 && rand() < 0.25 ? 1 + Math.floor(rand() * 2) : 0,
      attendanceDays: rand() < 0.12 ? 0 : Math.floor(years * (120 + rand() * 120)),
      documents: Math.floor(rand() * 6),
      failsToLoad: rand() * 100 < settings.failureRate ? pick(LOAD_ERRORS) : null,
    });
  }

  // Reporting lines: everyone reports to a team lead or the department's senior person, they
  // report to an executive, so --with-managers has whole chains to walk.
  const execs = out.filter((e) => e.departmentName === "Executive Team");
  const byDept = new Map<string, SimEmployee[]>();
  for (const e of out) (byDept.get(e.departmentName) ?? byDept.set(e.departmentName, []).get(e.departmentName)!).push(e);
  for (const [, people] of byDept) {
    const head = people.find((p) => p.role !== "INTERN" && p.role !== "CONTRACTOR");
    const leads = people.filter((p) => p.role === "TEAM_LEAD");
    for (const p of people) {
      if (p.departmentName === "Executive Team") { p.managerBayId = p === execs[0] ? null : execs[0]?.legacyBayUserId ?? null; continue; }
      if (p === head) { p.managerBayId = execs.length ? pick(execs).legacyBayUserId : null; continue; }
      const lead = leads.length && p.role !== "TEAM_LEAD" ? pick(leads) : head;
      p.managerBayId = lead && lead !== p ? lead.legacyBayUserId : null;
    }
  }
  return out;
}

export const HOLIDAY_ROWS = 48;   // company-wide: the holiday module is never scoped to a batch
